import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useInventoryManagement } from '../../lib/inventory-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Package, AlertTriangle, ArrowDownRight, ArrowUpRight, Search, Plus, Edit2, PackagePlus, Trash2 } from 'lucide-react';
import { formatRelativeTime, cn } from '../../lib/utils';
import { InventoryItemModal } from '../../components/admin/InventoryItemModal';
import { StockAdjustmentModal } from '../../components/admin/StockAdjustmentModal';
import type { VInventoryDashboard } from '../../lib/types';
export function InventoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    inventory,
    alerts,
    movements,
    loading: invLoading,
    createItem,
    updateItem,
    deleteItem,
    adjustStock,
    toggleItem
  } = useInventoryManagement(restaurant?.id);
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [search, setSearch] = useState('');
  // Modal state
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VInventoryDashboard | null>(null);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<VInventoryDashboard | null>(null);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-4689">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-4690" />
      </div>;
  }
  const criticalCount = alerts.filter(a => a.alert_level === 'CRITICAL').length;
  const lowCount = alerts.filter(a => a.alert_level === 'LOW').length;
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.ingredient_name.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'out') return item.stock_status === 'out';
    if (filter === 'low') return item.stock_status === 'low' || item.stock_status === 'out';
    return true;
  });
  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsItemModalOpen(true);
  };
  const handleOpenEdit = (item: VInventoryDashboard) => {
    setEditingItem(item);
    setIsItemModalOpen(true);
  };
  const handleOpenAdjust = (item: VInventoryDashboard) => {
    setAdjustingItem(item);
    setIsAdjustModalOpen(true);
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4691">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-4692">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4693">
          <div data-id="element-4694">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-4695">Inventario</h1>
            <p className="text-gray-500" data-id="element-4696">Control de stock e ingredientes</p>
          </div>
          <Button onClick={handleOpenCreate} data-id="element-4697">
            <Plus className="w-4 h-4 mr-2" data-id="element-4698" />
            Nuevo Ingrediente
          </Button>
        </div>

        {/* Alerts Summary */}
        {(criticalCount > 0 || lowCount > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4699">
            {criticalCount > 0 && <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-4" data-id="element-4700">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-4701">
                  <AlertTriangle className="w-6 h-6 text-red-600" data-id="element-4702" />
                </div>
                <div data-id="element-4703">
                  <h3 className="font-bold text-red-900" data-id="element-4704">Stock Crítico</h3>
                  <p className="text-red-700 text-sm" data-id="element-4705">
                    {criticalCount} ingredientes requieren atención inmediata
                  </p>
                </div>
              </div>}
            {lowCount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4" data-id="element-4706">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0" data-id="element-4707">
                  <Package className="w-6 h-6 text-amber-600" data-id="element-4708" />
                </div>
                <div data-id="element-4709">
                  <h3 className="font-bold text-amber-900" data-id="element-4710">Stock Bajo</h3>
                  <p className="text-amber-700 text-sm" data-id="element-4711">
                    {lowCount} ingredientes por debajo del mínimo sugerido
                  </p>
                </div>
              </div>}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4712">
          {/* Main Inventory Table */}
          <Card className="lg:col-span-2" data-id="element-4713">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4" data-id="element-4714">
              <CardTitle data-id="element-4715">Ingredientes</CardTitle>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-4716">
                <div className="relative" data-id="element-4717">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-4718" />
                  <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber w-full" data-id="element-4719" />
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-4720">
                  <button onClick={() => setFilter('all')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-4721">
                    Todos
                  </button>
                  <button onClick={() => setFilter('low')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'low' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-4722">
                    Bajos
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-4723">
              {invLoading ? <div className="space-y-4" data-id="element-4724">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-4725" />)}
                </div> : <div className="overflow-x-auto" data-id="element-4726">
                  <table className="w-full text-sm text-left" data-id="element-4727">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-4728">
                      <tr data-id="element-4729">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-4730">Ingrediente</th>
                        <th className="px-4 py-3 text-right" data-id="element-4731">Stock Actual</th>
                        <th className="px-4 py-3 text-right" data-id="element-4732">Mínimo</th>
                        <th className="px-4 py-3" data-id="element-4733">Estado</th>
                        <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-4734">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4735">
                      {filteredInventory.map(item => <tr key={item.inventory_item_id} className="border-b border-gray-50 last:border-0" data-id="element-4736">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-4737">
                            {item.ingredient_name}
                            {item.supplier && <div className="text-xs text-gray-400 font-normal" data-id="element-4738">
                                {item.supplier}
                              </div>}
                          </td>
                          <td className="px-4 py-3 text-right font-bold" data-id="element-4739">
                            {item.current_stock}{' '}
                            <span className="text-gray-500 font-normal" data-id="element-4740">
                              {item.unit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-4741">
                            {item.minimum_stock} {item.unit}
                          </td>
                          <td className="px-4 py-3" data-id="element-4742">
                            {item.stock_status === 'ok' ? <Badge variant="success" data-id="element-4743">OK</Badge> : item.stock_status === 'low' ? <Badge variant="warning" data-id="element-4744">Bajo</Badge> : <Badge variant="danger" data-id="element-4745">Agotado</Badge>}
                          </td>
                          <td className="px-4 py-3" data-id="element-4746">
                            <div className="flex items-center justify-end gap-1" data-id="element-4747">
                              <button onClick={() => handleOpenAdjust(item)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Ajustar stock" data-id="element-4748">
                                <PackagePlus className="w-4 h-4" data-id="element-4749" />
                              </button>
                              <button onClick={() => handleOpenEdit(item)} className="p-1.5 text-gray-400 hover:text-brand-amber hover:bg-amber-50 rounded-lg transition-colors" title="Editar ingrediente" data-id="element-4750">
                                <Edit2 className="w-4 h-4" data-id="element-4751" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                      {filteredInventory.length === 0 && <tr data-id="element-4752">
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500" data-id="element-4753">
                            No se encontraron ingredientes
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>}
            </CardContent>
          </Card>

          {/* Recent Movements */}
          <Card data-id="element-4754">
            <CardHeader data-id="element-4755">
              <CardTitle data-id="element-4756">Movimientos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-4757">
              {invLoading ? <Skeleton className="h-64 w-full" data-id="element-4758" /> : movements.length > 0 ? <div className="space-y-4" data-id="element-4759">
                  {movements.map(mov => {
                const item = inventory.find(i => i.inventory_item_id === mov.inventory_item_id);
                const isIncrease = mov.movement_type === 'restock';
                return <div key={mov.id} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-4760">
                        <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5', isIncrease ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')} data-id="element-4761">
                          {isIncrease ? <ArrowUpRight className="w-4 h-4" data-id="element-4762" /> : <ArrowDownRight className="w-4 h-4" data-id="element-4763" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4764">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4765">
                            {item?.ingredient_name || 'Item desconocido'}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4766">
                            {mov.movement_type === 'sale_consumption' ? 'Consumo por venta' : mov.movement_type === 'restock' ? 'Reabastecimiento' : mov.movement_type === 'waste' ? 'Desperdicio' : 'Ajuste manual'}
                            {mov.reason && ` · ${mov.reason}`}
                          </p>
                        </div>
                        <div className={cn('text-sm font-bold shrink-0', isIncrease ? 'text-emerald-600' : 'text-red-600')} data-id="element-4767">
                          {isIncrease ? '+' : '-'}
                          {Math.abs(mov.quantity)} {item?.unit}
                        </div>
                      </div>;
              })}
                </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-4768">
                  No hay movimientos recientes
                </p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Item Create/Edit Modal */}
      <InventoryItemModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} item={editingItem} onSave={async data => {
      let result;
      if (editingItem) {
        result = await updateItem(editingItem.inventory_item_id, data);
      } else {
        result = await createItem({
          ...data,
          restaurant_id: restaurant.id
        });
      }
      if (result?.success) {
        setIsItemModalOpen(false);
      }
      return result;
    }} onDelete={editingItem ? async () => {
      if (confirm('¿Estás seguro de eliminar este ingrediente? Se perderán todos los movimientos asociados.')) {
        const result = await deleteItem(editingItem.inventory_item_id);
        if (result?.success) {
          setIsItemModalOpen(false);
        }
        return result;
      }
      return {
        success: false
      };
    } : undefined} data-id="element-4769" />

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal isOpen={isAdjustModalOpen} onClose={() => setIsAdjustModalOpen(false)} item={adjustingItem} onAdjust={adjustStock} data-id="element-4770" />
    </AdminLayout>;
}